import { defineStore } from "pinia";

import { ISector } from "../models/sector"
import { ISectorFilter } from "../models/filters/sectorFilter"
import SectorService from "../services/SectorService";
import { ISectorForm } from "../models/forms/sectorForm";

import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import i18n from "@/i18n";
import { IBuilding } from "../models/building";

export const useSectorStore = defineStore("sectors", {
    state: () => ({
        datas: [] as Array<ISector>,
        current : {} as ISector,
        itemForm : {} as ISectorForm,
        arrayForm: [] as ISectorForm[],
        isLoading: false as boolean,
        buildingId: 0 as number,
    }),
    getters: {
        getByBuildingId(state): ISector[] {
            // par ordre alphabétique
            // Use buildingId instead of building.id to avoid undefined access
            return state.datas.filter((sector) => sector.buildingId === state.buildingId).sort((a, b) => a.name.localeCompare(b.name));
        },

        getUniqueRandomSectorColor(state) {
            let color: string;
            do {
                color = `#${Math.floor(Math.random() * 0xFFFFFF)
                    .toString(16)
                    .padStart(6, "0")}`;
            } while (state.arrayForm.some(s => s.color.toLocaleUpperCase() == color.toLocaleUpperCase()));

            return color;
        }
    },
    actions: {

        /**
         * Chargement des données dans le store
         * @param filter Le filtre à appliquer pour le chargement des données
         * @returns
         */
        async load(filter: ISectorFilter) {
            this.isLoading = true;
            let result = await SectorService.getAll<ISectorFilter, ISector>(filter);
            this.isLoading = false;
            this.datas = [];
            if (!result.error) {
                var resultData = result.data as ISector[];
                this.datas = resultData;
            }
            return result;
        },

        /**
         * Récupération d'un secteur par son identifiant
         * @param id
         * @returns
         */
        async get(id: number) {
            let result = await SectorService.getById<ISector>(id);
            // mettre à jour dans le store ?
            if ( !result.error) {
                let index = this.datas.findIndex((a) => a.id == id);
                if (index >= 0)
                    this.datas[index] = result.data as ISector;
                // on le stock dans le current puis dans le form
                this.current = result.data as ISector;
                this.itemForm = this.toForm(result.data as ISector);
            }
            return result;
        },

        /**
        * Récupération de l'objet dans le store
        * @param id Identifiant de l'objet
        * @returns
        */
        async searchStoreById (id: number) {
            this.current = this.datas.find((objet) => objet.id == id)!;
            this.itemForm = await this.toForm(this.current as ISector);
            return this.current;
        },

        /**
         * Mise à jour ou création d'un objet
         * @param maj
         * @returns
         */
        async save(maj:boolean = true, isPatch:boolean = false)
        {
            let result = {} as ServiceOperationResult<ISector | IDataOperationResult<ISector>>;
            if ( this.itemForm.id > 0) {
                result = await SectorService.update(this.itemForm);

                if ( !result.error)
                {
                    this.current = result.data as ISector;
                    if ( maj ) {
                        let index = this.datas.findIndex((a) => a.id == this.itemForm.id);
                        if (index >= 0) {
                            this.datas[index] = this.current;
                        } else {
                            const error = new DataOperationError();
                            error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                            return new ServiceOperationResult<IDataOperationResult<ISector>>(true, {errors: error});
                        }
                    }
                }
            } else {
                result = await SectorService.create(this.itemForm);
                if ( !result.error)
                {
                    this.current = result.data as ISector;
                    this.datas.push(this.current);
                }

            }
            return result;
        },
        /**
         * Création en batch d'éléments en fonction de leurs données
         * @param datas liste des objets à créer
         * @param maj
         * @returns
         */
        async batchSave(datas:ISectorForm[], maj:boolean = true) {
            const returnItems: ISector[] = [];
            const createItems = datas.filter(d => d.id == 0) as ISectorForm[];
            const updateItems = datas.filter(d => d.id > 0) as ISectorForm[];
            if ( createItems.length > 0) {
                let result = await SectorService.batchCreate(createItems);
                if ( !result.error && maj)
                {
                    const datas = result.data as ISector[];
                    datas.forEach((item) => {
                        this.datas.push(item);
                        returnItems.push(item);
                    });
                }
            }
            if ( updateItems.length > 0) {
                let result = await SectorService.batchUpdate(updateItems);
                if (!result.error && maj) {
                    const datas = result.data as ISector[];
                    datas.forEach((item) => {
                        let index = this.datas.findIndex((a) => a.id == item.id);
                        if (index >= 0) {
                            this.datas[index] = item;
                            returnItems.push(item);
                        } else {
                            const error = new DataOperationError();
                            error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                            return new ServiceOperationResult<IDataOperationResult<ISector>>(true, {errors: error});
                        }
                    });
                }
            }
            return returnItems;
        },

        /**
         * Delete de l'objet
         * @returns
         */
        async delete() {
            let result = await SectorService.delete<ISector>(this.current.id , true);

            if ( !result.error) {
                let index = this.datas.findIndex((a) => a.id == this.current.id);
                if (index >= 0) {
                    // On remove l'élément des données
                    this.datas.splice(index, 1);
                }

                this.current = this.newItem();
                this.itemForm = this.newForm();
            }
            return result;
        },

        async batchDelete(ids:number[]) {
            const result = await SectorService.batchDelete(ids , true);
            if (!result.error) {
                this.datas = this.datas.filter(item => !ids.includes(item.id));
            }
            return result;
        },

        /**
         * Transformation de l'objet en formulaire
         * @param item Batiment
         * @returns
         */
        toForm(item: ISector): ISectorForm {
            const form = {} as ISectorForm;

            form.id = item.id;

            form.color = item.color;
            form.name = item.name;

            // Clés étrangères
            // Use buildingId property directly instead of navigation property
            form.buildingId = item.buildingId;

            this.itemForm = form;
            return form;
        },

        /**
         * Permet d'initialiser un objet vide pour le formulaire
         * @returns
         */
        newForm(): ISectorForm {
            this.itemForm = {
                id: 0,
                color: "",
                name: "",
                buildingId: 0,
            } as ISectorForm;
            return this.itemForm;
        },

        /**
         *  Permet d'initialiser un objet vide pour les referentiels
         * @returns
         */
        newItem(): ISector {

            this.current = {
                id: 0,
                color: "",
                name: "",
                buildingId: 0,
            } as ISector;

            this.itemForm = this.toForm(this.current);
            return this.current;
        },

        removeFromFormAtIndex(i: number) {
            this.arrayForm.splice(i, 1);
        },

        getSectorColorByIndex(i: number): string {
            return this.arrayForm.length > 0 ? this.arrayForm[i].color : '#FFFFFF';
        }
    }
})
